import { useState, useEffect, useRef, useCallback, type FormEvent } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { BookOpen, Eye, EyeOff, HelpCircle, MessageCircle, X, XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { SEO } from '@/components/SEO';
import { SplashScreen } from '@/components/SplashScreen';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { validateEmail, normalizeEmail } from '@/lib/validators';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';

import { useAuth } from '../contexts/AuthContext';
import { toast } from '../lib/toast';
import { usePageTitle } from '../hooks/usePageTitle';
import { ThemeToggle } from '../components/ThemeToggle';

const HERO_IMAGE_URL = '/homem.png';
const MIN_INITIAL_SPLASH_MS = 1600;
const MIN_POST_LOGIN_SPLASH_MS = 900;
const SPLASH_STORAGE_KEY = 'stant:login-splash-seen';
const SPECIALIST_WHATSAPP_URL =
  'https://wa.me/558293122559?text=Ol%C3%A1%21%20Tenho%20interesse%20no%20Atas%20Digitais%20Stant%20e%20gostaria%20de%20falar%20com%20um%20especialista.';

export default function Login() {
  usePageTitle('Entrar');
  const navigate = useNavigate();
  const location = useLocation();
  const { login, user, loading: authLoading } = useAuth();
  const { t } = useTranslation('auth');

  useEffect(() => {
    if ((location.state as any)?.accountInactive) {
      toast.error('Sua conta foi desativada. Entre em contato com o administrador.');
      // Limpa o state para não reaparecer num reload
      window.history.replaceState({}, '');
    }
  }, []);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [helpMenuOpen, setHelpMenuOpen] = useState(false);
  
  const [emailError, setEmailError] = useState('');
  const [emailTouched, setEmailTouched] = useState(false);

  const [shouldShowInitialSplash] = useState(() => {
    if (typeof window === 'undefined') return false;
    return sessionStorage.getItem(SPLASH_STORAGE_KEY) !== 'true';
  });

  const [splashVariant, setSplashVariant] = useState<'initial' | 'post-login' | null>(
    shouldShowInitialSplash ? 'initial' : null
  );
  const [splashVisible, setSplashVisible] = useState(shouldShowInitialSplash);
  const [contentReady, setContentReady] = useState(!shouldShowInitialSplash);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [initialTimerElapsed, setInitialTimerElapsed] = useState(!shouldShowInitialSplash);

  const hideTimeoutRef = useRef<number | undefined>(undefined);
  const helpMenuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        window.clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!helpMenuOpen) return;

    const handlePointerDown = (event: PointerEvent) => {
      if (!helpMenuRef.current?.contains(event.target as Node)) {
        setHelpMenuOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setHelpMenuOpen(false);
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [helpMenuOpen]);

  const hideSplash = useCallback(
    (options?: { revealContent?: boolean }) => {
      if (!splashVariant) {
        if (options?.revealContent) {
          setContentReady(true);
        }
        return;
      }

      setSplashVisible(false);

      if (hideTimeoutRef.current) {
        window.clearTimeout(hideTimeoutRef.current);
      }

      const variantAtRequest = splashVariant;

      hideTimeoutRef.current = window.setTimeout(() => {
        setSplashVariant((current) => {
          if (current !== variantAtRequest) {
            return current;
          }

          if (options?.revealContent) {
            setContentReady(true);
          }

          return null;
        });
      }, 450);
    },
    [splashVariant]
  );

  const showSplash = useCallback((variant: 'initial' | 'post-login') => {
    setSplashVariant(variant);
    requestAnimationFrame(() => {
      setSplashVisible(true);
    });
  }, []);

  useEffect(() => {
    const image = new Image();
    image.src = HERO_IMAGE_URL;

    if (image.complete) {
      setImageLoaded(true);
    } else {
      image.onload = () => setImageLoaded(true);
      image.onerror = () => setImageLoaded(true);
    }

    return () => {
      image.onload = null;
      image.onerror = null;
    };
  }, []);

  useEffect(() => {
    if (!shouldShowInitialSplash) return;

    const timer = window.setTimeout(() => setInitialTimerElapsed(true), MIN_INITIAL_SPLASH_MS);
    return () => window.clearTimeout(timer);
  }, [shouldShowInitialSplash]);

  useEffect(() => {
    if (splashVariant !== 'initial') return;
    if (!imageLoaded || !initialTimerElapsed) return;

    hideSplash({ revealContent: true });
    if (typeof window !== 'undefined') {
      sessionStorage.setItem(SPLASH_STORAGE_KEY, 'true');
    }
  }, [hideSplash, imageLoaded, initialTimerElapsed, splashVariant]);

  useEffect(() => {
    if (!authLoading && user) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, authLoading, navigate]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (loading) return;

    setLoading(true);
    setError('');
    setEmailTouched(true);


    const emailValidation = validateEmail(email);
    if (!emailValidation.isValid) {
      setEmailError(emailValidation.error || '');
      setLoading(false);
      return;
    }
    setEmailError('');


    if (!password || password.length < 6) {
      setError(t('login.password_required'));
      setLoading(false);
      return;
    }

    try {
      const normalizedEmail = normalizeEmail(email);
      await login(normalizedEmail, password, rememberMe);
      showSplash('post-login');
      await new Promise<void>((resolve) => window.setTimeout(resolve, MIN_POST_LOGIN_SPLASH_MS));
      navigate('/dashboard');
    } catch (err) {
      console.error('Login error:', err);

      if (err instanceof Error) {
        const msg = err.message || '';
        if (msg.includes('Invalid login credentials')) {
          setError(t('login.invalid_credentials'));
        } else if (msg.includes('Email not confirmed')) {
          setError(t('login.email_not_confirmed'));
        } else if (msg.includes('Network') || msg.includes('network') || msg.includes('ERR_NETWORK')) {
          setError('Sem conexão com a internet. Verifique sua rede e tente novamente.');
        } else if (msg.includes('timeout') || msg.includes('TIMEOUT')) {
          setError('O servidor demorou muito para responder. Tente novamente em alguns segundos.');
        } else {
          setError(msg);
        }
      } else {
        setError(t('login.error_generic'));
      }

      hideSplash();
    } finally {
      setLoading(false);
    }
  };

  const handleEmailChange = (value: string) => {
    setEmail(value);
    if (emailTouched) {
      const validation = validateEmail(value);
      setEmailError(validation.error || '');
    }
  };

  const handleNavigateToDocs = async () => {
    setHelpMenuOpen(false);
    showSplash('post-login');
    await new Promise<void>((resolve) => window.setTimeout(resolve, MIN_POST_LOGIN_SPLASH_MS));
    navigate('/docs');
  };

  const handleSpecialistClick = () => {
    setHelpMenuOpen(false);
    window.open(SPECIALIST_WHATSAPP_URL, '_blank', 'noopener,noreferrer');
  };

  const splashSubtitle =
    splashVariant === 'post-login' ? t('login.splash_loading') : t('login.splash_preparing');

  return (
    <div className="relative flex min-h-screen bg-background">
      <SEO
        title="Login, Acesse sua conta"
        description="Faça login no Atas Digitais para gerenciar suas atas de reunião de obra. Sistema de atas digitais para construtoras, fiscalizadoras e gerenciadoras. Registre decisões e acompanhe pendências."
        keywords="login atas digitais, entrar atas, acessar conta, ata de reunião login, sistema de atas"
        canonical="/login"
      />

      {splashVariant && (
        <SplashScreen
          visible={splashVisible}
          variant={splashVariant}
          subtitle={splashSubtitle}
        />
      )}

      <div
        className={cn(
          'flex w-full flex-col lg:flex-row transition-opacity duration-700 ease-out',
          contentReady ? 'opacity-100 delay-150' : 'pointer-events-none opacity-0'
        )}
      >
        <div className="relative hidden flex-1 overflow-hidden lg:block">
          <div className="absolute inset-0 bg-gradient-to-br from-[#001A72] via-[#013EFB] to-[#0A7AFF]" />

          {/* Decorative blobs */}
          <div className="absolute -top-32 -left-32 w-[500px] h-[500px] rounded-full bg-white/5 blur-3xl" />
          <div className="absolute bottom-0 right-0 w-[400px] h-[400px] rounded-full bg-[#0A7AFF]/40 blur-3xl" />

          {/* Subtle grid */}
          <div
            className="absolute inset-0 opacity-[0.04]"
            style={{
              backgroundImage:
                'linear-gradient(white 1px, transparent 1px), linear-gradient(90deg, white 1px, transparent 1px)',
              backgroundSize: '48px 48px',
            }}
          />

          {/* Content — centered */}
          <div className="relative z-10 flex flex-col items-center justify-between h-full px-12 py-12 text-center">
            {/* Spacer top */}
            <div />

            {/* Center: message + button */}
            <div className="flex flex-col items-center gap-5">
              <p className="text-white/70 text-sm">
                Tem alguma dúvida? Fale com um especialista.
              </p>

              <a
                href={SPECIALIST_WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center gap-2.5 px-5 py-3 rounded-xl bg-white/10 border border-white/25 text-white text-sm font-medium backdrop-blur-sm transition-all duration-200 hover:bg-white/20 hover:border-white/40 hover:scale-[1.04] active:scale-[0.97]"
              >
                <MessageCircle className="size-4 text-white/80 group-hover:text-white transition-colors" />
                Fale com um especialista
              </a>
            </div>

            {/* Bottom: Powered by */}
            <a
              href="https://www.stant.com.br/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center gap-2 cursor-pointer group"
            >
              <span className="text-white/40 text-xs transition-colors duration-200 group-hover:text-white/60">Powered by</span>
              <img
                src="/logo-stant-controle.png"
                alt="stant Controle"
                className="h-5 w-auto brightness-0 invert opacity-60 transition-all duration-300 group-hover:opacity-90 group-hover:scale-110"
              />
            </a>
          </div>
        </div>

        <div className="flex flex-1 items-center justify-center px-6 py-12 sm:px-12">
          <div className="w-full max-w-md">
            {/* Language Switcher + Theme Toggle */}
            <div className="flex justify-end items-center gap-1 mb-4">
              <ThemeToggle />
              <LanguageSwitcher />
            </div>

            {/* Logo fora do Card */}
            <img
              src="/logo-stant.svg"
              alt="Stant"
              className="mx-auto w-44 h-auto mb-8 dark:brightness-0 dark:invert"
            />

            <Card className="bg-card/90 shadow-xl shadow-black/5 backdrop-blur-sm">
              <CardHeader className="text-center space-y-2">
                <CardTitle className="text-2xl font-bold">{t('login.title')}</CardTitle>
                <CardDescription>
                  {t('login.subtitle')}
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="email">{t('login.email')}</Label>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    placeholder={t('login.email_placeholder')}
                    value={email}
                    onChange={(event) => handleEmailChange(event.target.value)}
                    onBlur={() => {
                      setEmailTouched(true);
                      const validation = validateEmail(email);
                      setEmailError(validation.error || '');
                    }}
                    required
                    disabled={loading}
                    className={`h-11 transition-transform duration-200 ease-out focus-visible:scale-[1.01] ${
                      emailError && emailTouched ? 'border-[#013EFB] focus-visible:ring-[#013EFB]' : ''
                    }`}
                  />
                  {emailError && emailTouched && (
                    <div className="flex items-start gap-1.5 text-sm text-[#013EFB]">
                      <XCircle size={16} className="mt-0.5 flex-shrink-0" />
                      <span>{emailError}</span>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">{t('login.password')}</Label>

                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      placeholder={t('login.password_placeholder')}
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      required
                      disabled={loading}
                      className="h-11 pr-12 transition-transform duration-200 ease-out focus-visible:scale-[1.01]"
                    />

                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      onClick={() => setShowPassword((value) => !value)}
                      disabled={loading}
                      aria-pressed={showPassword}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground transition-transform duration-200 hover:scale-105"
                    >
                      {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                    </Button>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="rememberMe"
                      checked={rememberMe}
                      onCheckedChange={(checked) => setRememberMe(checked === true)}
                      disabled={loading}
                    />
                    <Label
                      htmlFor="rememberMe"
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      {t('login.remember_me')}
                    </Label>
                  </div>
                  <Link
                    to="/reset-password"
                    className="text-sm font-medium text-primary hover:text-primary/80 transition-colors"
                  >
                    {t('login.forgot_password')}
                  </Link>
                </div>

                {error && (
                  <div
                    role="alert"
                    className="rounded-lg border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive shadow-sm"
                  >
                    {error}
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={loading}
                  className="h-11 w-full text-base font-semibold transition-all duration-300 ease-out hover:-translate-y-[1px] hover:shadow-lg disabled:hover:translate-y-0"
                >
                  {loading ? t('login.submitting') : t('login.submit')}
                </Button>
              </form>

              <div className="text-center text-sm text-muted-foreground">
                {t('login.no_account')}
                <Button
                  type="button"
                  variant="link"
                  onClick={() => navigate('/register')}
                  className="ml-1 font-semibold"
                >
                  {t('login.create_account')}
                </Button>
              </div>
            </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <div
        ref={helpMenuRef}
        className={cn(
          'fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3',
          'transition-all duration-300 ease-out',
          contentReady ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        )}
      >
        {helpMenuOpen && (
          <div
            className={cn(
              'w-[min(calc(100vw-3rem),320px)] overflow-hidden rounded-2xl border border-border/80 bg-popover shadow-2xl shadow-black/10',
              'animate-in fade-in slide-in-from-bottom-2 duration-200'
            )}
            role="dialog"
            aria-label="Ações de ajuda"
          >
            <div className="flex items-center justify-between border-b border-border/70 px-4 py-3">
              <div>
                <p className="text-sm font-semibold text-foreground">Precisa de ajuda?</p>
                <p className="text-xs text-muted-foreground">Escolha uma ação para continuar.</p>
              </div>
              <button
                type="button"
                onClick={() => setHelpMenuOpen(false)}
                className="rounded-full p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                aria-label="Fechar ajuda"
              >
                <X className="size-4" />
              </button>
            </div>

            <div className="grid gap-2 p-3">
              <button
                type="button"
                onClick={handleSpecialistClick}
                className="flex w-full items-center gap-3 rounded-xl border border-border bg-card px-3 py-3 text-left transition-colors hover:border-primary/30 hover:bg-primary/5 focus:outline-none focus:ring-2 focus:ring-primary/20"
              >
                <span className="flex size-10 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
                  <MessageCircle className="size-5" />
                </span>
                <span>
                  <span className="block text-sm font-semibold text-foreground">Fale com especialista</span>
                  <span className="block text-xs text-muted-foreground">Abra uma conversa pelo WhatsApp.</span>
                </span>
              </button>

              <button
                type="button"
                onClick={handleNavigateToDocs}
                className="flex w-full items-center gap-3 rounded-xl border border-border bg-card px-3 py-3 text-left transition-colors hover:border-primary/30 hover:bg-primary/5 focus:outline-none focus:ring-2 focus:ring-primary/20"
              >
                <span className="flex size-10 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <BookOpen className="size-5" />
                </span>
                <span>
                  <span className="block text-sm font-semibold text-foreground">Como utilizar</span>
                  <span className="block text-xs text-muted-foreground">Veja o guia de uso do sistema.</span>
                </span>
              </button>
            </div>
          </div>
        )}

        <button
          type="button"
          onClick={() => setHelpMenuOpen((open) => !open)}
          className={cn(
            'flex size-14 items-center justify-center rounded-full',
            'bg-foreground text-background shadow-lg shadow-black/15',
            'dark:bg-background dark:text-foreground dark:border-2 dark:border-foreground/30',
            'transition-all duration-300 ease-out hover:scale-105 hover:bg-foreground/90 dark:hover:bg-muted hover:shadow-xl',
            'focus:outline-none focus:ring-2 focus:ring-foreground/30 focus:ring-offset-2'
          )}
          aria-label={helpMenuOpen ? 'Fechar ajuda' : 'Abrir ajuda'}
          aria-expanded={helpMenuOpen}
        >
          {helpMenuOpen ? <X className="size-6" /> : <HelpCircle className="size-6" />}
        </button>
      </div>
    </div>
  );
}
